// What every Agents screen shows the same way (M3-08; agents-in-chat research §3 recs 6, 7, 15,
// 16): a run's state badge, its wake-up source, the cost chip (model path, tokens, cost, the
// budget it reserved; never "$0" for a run that spent), a DE's one-line status, and the honest
// "not live on this server yet" page for the routes whose build has not landed.
import {
  Badge,
  Button,
  cn,
  EmptyState,
  InfoTip,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { RunRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { useWalletOn } from "../billing/use-wallet-on";
import { formatDateTime } from "../format";
import { companyQuery } from "../queries";
import { formatMicros, formatTokens, UNREPORTED_HELP, usageUnreported } from "./money";

export { formatMicros, formatTokens, UNREPORTED_HELP, usageUnreported } from "./money";

export type RunState = RunRow["state"];

export const RUN_STATE_LABELS: Record<RunState, string> = {
  queued: "Queued",
  running: "Running",
  waiting_input: "Waiting for input",
  awaiting_approval: "Awaiting approval",
  waiting_children: "Waiting on delegates",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const RUN_STATE_TONES: Record<
  RunState,
  "accent" | "info" | "warning" | "success" | "danger" | "neutral"
> = {
  queued: "info",
  running: "accent",
  waiting_input: "warning",
  awaiting_approval: "warning",
  waiting_children: "info",
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
};

/** The states a run can still be stopped in (02 §run: everything before an end). */
export const LIVE_STATES: readonly RunState[] = [
  "queued",
  "running",
  "waiting_input",
  "awaiting_approval",
  "waiting_children",
];
export const isLive = (state: RunState) => LIVE_STATES.includes(state);

export function RunStateBadge({ state }: { state: RunState }) {
  return (
    <Badge variant={RUN_STATE_TONES[state]} dot={isLive(state)} data-run-state={state}>
      {RUN_STATE_LABELS[state]}
    </Badge>
  );
}

export const WAKEUP_KIND_LABELS: Record<RunRow["trigger_kind"], string> = {
  mention: "Mention",
  heartbeat: "Heartbeat",
  inbox: "Inbox item",
  approval: "Approval decided",
  delegation: "Delegated work",
  manual: "Started by hand",
  drift_audit: "Drift audit",
  pre_read: "Meeting pre-read",
  room_turn: "Agent room turn",
  status_ask: "Status ask",
  reporting: "Reporting duty",
  board: "Board check",
};

const SOURCE_LABELS: Record<NonNullable<RunRow["trigger_ref"]>["kind"], string> = {
  message: "a message",
  topic: "a topic",
  task: "a task",
  approval: "an approval",
  delegation_request: "a delegation request",
  inbox_item: "an inbox item",
  schedule: "the schedule",
  run: "another run",
  manual: "a person",
  agent_room: "an agent room",
  status_ask: "a status ask",
  reporting_duty: "a reporting duty",
  meeting: "a meeting",
  chat_ask: "a question from search",
  de_brief: "a brief",
  board: "a board it owns",
  commitment: "a commitment",
};

/** "Inbox item · from a task" — what woke the agent and where it came from. */
export function wakeupSource(kind: RunRow["trigger_kind"], ref: RunRow["trigger_ref"]): string {
  // AGENT-REPORTING: one phrase, not "Reporting duty · from a reporting duty".
  if (kind === "reporting") return "Reporting duty (a KPI is due)";
  const what = WAKEUP_KIND_LABELS[kind];
  return ref ? `${what} · from ${SOURCE_LABELS[ref.kind]}` : what;
}

/**
 * Metered cost with its tokens (founder review 2026-10-27): "$0.00052 · 1,234 tokens", in/out
 * behind the (i). Usage nobody reported (self-hosted runs) reads "—", never "$0.00".
 */
export function UsageCost({
  usage,
  currency,
  live = false,
  hideTokens = false,
  className,
}: {
  usage: { cost_micros: number; tokens_in: number; tokens_out: number };
  currency: string | undefined;
  /** A run still going: no usage yet is "nothing yet", not "not reported". */
  live?: boolean;
  /** A wallet company: dollars only, token counts live in the wallet entry detail. */
  hideTokens?: boolean;
  className?: string;
}) {
  if (usageUnreported(usage))
    return live ? (
      <span className={cn("tabular-nums", className)} data-slot="usage-cost">
        —
      </span>
    ) : (
      <span
        className={cn("inline-flex items-center gap-1 tabular-nums", className)}
        data-slot="usage-cost"
        data-unreported=""
      >
        —<InfoTip label="About cost">{UNREPORTED_HELP}</InfoTip>
      </span>
    );
  const tokens = hideTokens ? 0 : usage.tokens_in + usage.tokens_out;
  return (
    <span
      className={cn("inline-flex items-center gap-1 tabular-nums", className)}
      data-slot="usage-cost"
    >
      {formatMicros(usage.cost_micros, currency)}
      {/* Phones drop the inline total for room; the (i) with the counts stays (Codex WF-POLISH
          r1 #5). */}
      {tokens ? (
        <span className="inline-flex items-center gap-1">
          <span className="text-muted-foreground max-md:hidden">
            · {formatTokens(tokens)} tokens
          </span>
          <InfoTip label="About tokens">
            {formatTokens(tokens)} tokens: {formatTokens(usage.tokens_in)} in ·{" "}
            {formatTokens(usage.tokens_out)} out
          </InfoTip>
        </span>
      ) : null}
    </span>
  );
}

/** The company's reporting currency (cached company detail; every Agents screen shares it). */
export function useCurrency(api: Api, co: string, enabled = true): string | undefined {
  return useQuery({ ...companyQuery(api, co), enabled }).data?.reporting_currency;
}

/**
 * The currency for run-level and aggregate costs: in wallet view they are wallet charges in USD,
 * whatever the company's reporting currency is now (it may change after the wallet went off).
 */
export function useCostCurrency(api: Api, co: string, enabled = true): string | undefined {
  const reporting = useCurrency(api, co, enabled);
  return useWalletOn(api, co, enabled) ? "USD" : reporting;
}

/** "4 min", "2 h 05 min", "3 d" between two instants (or until now). */
export function formatElapsed(fromIso: string, toIso?: string | null, now = Date.now()): string {
  const ms = Math.max(0, (toIso ? Date.parse(toIso) : now) - Date.parse(fromIso));
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ${String(min % 60).padStart(2, "0")} min`;
  return `${Math.floor(h / 24)} d`;
}

/**
 * The cost chip (rec 15): the run's cost; opening it shows the model path, tokens and what the
 * run reserved. A run on the subscription path is "estimated", never shown as free.
 */
export function CostChip({
  run,
  currency,
  hideTokens = false,
}: {
  /** A wallet company: dollars only, no token counts. */
  hideTokens?: boolean;
  run: Pick<
    RunRow,
    | "cost_micros"
    | "tokens_in"
    | "tokens_out"
    | "model"
    | "model_path"
    | "model_path_fallback_used"
    | "budget_reserved_micros"
    | "state"
  >;
  currency: string | undefined;
}) {
  // No usage on a live run is "nothing yet"; on an ended run, the runtime never reported it.
  const noUsage = usageUnreported(run);
  const unreported = noUsage && !isLive(run.state);
  const estimated = !noUsage && run.model_path === "subscription";
  const cost = noUsage ? "—" : formatMicros(run.cost_micros, currency);
  const tokens = hideTokens ? 0 : run.tokens_in + run.tokens_out;
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="xs"
            data-slot="cost-chip"
            aria-label={
              unreported
                ? "Cost not reported: details"
                : noUsage
                  ? "No cost yet: details"
                  : `Cost ${cost}${estimated ? " (estimated)" : ""}: details`
            }
            data-unreported={unreported ? "" : undefined}
          />
        }
      >
        <span className="tabular-nums">{estimated ? `≈ ${cost}` : cost}</span>
        {tokens ? (
          <span className="text-muted-foreground tabular-nums">
            · {formatTokens(tokens)} tokens
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <PopoverHeader>
          <PopoverTitle>Run cost</PopoverTitle>
          <PopoverDescription>
            {unreported
              ? UNREPORTED_HELP
              : noUsage
                ? "Nothing metered yet."
                : estimated
                  ? "Subscription path: the cost is an estimate from the tokens used."
                  : "Gateway path: metered by the model gateway."}
          </PopoverDescription>
        </PopoverHeader>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
          {hideTokens ? null : (
            <>
              <dt className="text-muted-foreground">Model</dt>
              <dd>{run.model ?? "Not chosen yet"}</dd>
              <dt className="text-muted-foreground">Path</dt>
              <dd>
                {run.model_path === "gateway" ? "Gateway" : "Subscription"}
                {run.model_path_fallback_used ? " (fallback)" : ""}
              </dd>
              <dt className="text-muted-foreground">Tokens</dt>
              <dd className="tabular-nums">
                {unreported
                  ? "Not reported"
                  : noUsage
                    ? "None yet"
                    : `${formatTokens(run.tokens_in)} in · ${formatTokens(run.tokens_out)} out`}
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">Cost</dt>
          <dd className="tabular-nums">{cost}</dd>
          <dt className="text-muted-foreground">Reserved</dt>
          <dd className="tabular-nums">
            {isLive(run.state) ? formatMicros(run.budget_reserved_micros, currency) : "Settled"}
          </dd>
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/**
 * A DE's status line (rec 6): what it is doing now, for how long, and a link to the run; idle
 * when its latest run has ended.
 */
export function StatusLine({
  run,
  slug,
  now = Date.now(),
}: {
  run: RunRow | undefined;
  slug: string;
  now?: number;
}) {
  if (!run)
    return (
      <span className="text-sm text-muted-foreground" data-slot="status-line" data-state="none">
        No runs yet
      </span>
    );
  const live = isLive(run.state);
  const since = run.started_at ?? run.created_at;
  return (
    <span
      className="inline-flex min-w-0 flex-wrap items-center gap-2 text-sm"
      data-slot="status-line"
      data-state={live ? "live" : "idle"}
    >
      {live ? (
        <>
          <RunStateBadge state={run.state} />
          <span className="text-muted-foreground tabular-nums">
            {formatElapsed(since, null, now)}
          </span>
          <Link
            to="/c/$slug/agents/runs/$id"
            params={{ slug, id: run.id }}
            search={keepFocus}
            className="text-primary-text underline-offset-4 hover:underline"
          >
            View run
          </Link>
        </>
      ) : (
        <span className="text-muted-foreground">
          Idle · last run {RUN_STATE_LABELS[run.state].toLowerCase()}{" "}
          {formatDateTime(run.ended_at ?? run.updated_at)}
        </span>
      )}
    </span>
  );
}

/** The page of a part of Agents whose server build has not landed on this API. */
export function NotLiveYet({
  icon,
  what,
  build,
}: {
  icon: LucideIcon;
  what: string;
  build: string;
}) {
  return (
    <EmptyState
      icon={icon}
      title={`${what} are not live on this server yet`}
      titleAs="h1"
      data-page="not-live"
      description={`They arrive with ${build}. Nothing is lost: this page fills in once the server serves them.`}
    />
  );
}
